import { Flex } from "@chakra-ui/layout";
import {
  Button,
  IconButton,
  Modal,
  Tooltip,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  AiOutlineCloudUpload,
  AiOutlineDelete,
  AiOutlineDownload,
  AiFillFileExcel,
  AiOutlineQrcode,
  AiOutlineSetting,
} from "react-icons/ai";
import { GoCheck, GoDiffAdded } from "react-icons/go";
import { MdBlock } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { BiCheckCircle } from "react-icons/bi"; // Add to existing imports
import ConfirmCsvConfirmation from "./confirmCsvConfirmation/ConfirmCsvConfirmation";
import DeleteConfirmation from "../../../components/confirmation/DeleteConfirmation";
import UpdateThresholdConfirmation from "../../../components/confirmation/UpdateThresholdConfirmation";
import EditableTable from "../../../components/editableTable/EditableTable";
import {
  getEventsInfo,
  deleteEvent,
  downloadQrs,
  getCertData,
  downloadScanStats,
  updateThreshold,
} from "../../../slice/certificateIssuerHomeSlice";
import {
  DownloadStatus,
  IssueCertificatesStatus,
  UploadStatusEnum,
  DisplayCsvStatus,
  DownloadQrStatus,
  DownloadExcelStatus,
  UpdateThresholdStatus,
  ConfirmCsvStatus,
} from "../../../utils/enums";
import { processAndDownloadExcel } from "../../../utils/utils";

import CreateEventConfirmation from "./createEventConfirmation/CreateEventConfirmation";
import FileUpload from "./fileUpload/FileUpload";
import IssueCertsConfirmation from "./issueCertsConfirmation/IssueCertsConfirmation";

import { useQueryState, parseAsInteger } from "nuqs";

function EventsInfoTable() {
  const dispatch = useDispatch();
  const [fileUploadInfo, setFileUploadInfo] = useState({});
  const [fileUploadType, setFileUploadType] = useState("csv");
  const [deleteEventId, setDeleteEventId] = useState("");
  const [deleteEventName, setDeleteEventName] = useState("");
  const { events: eventsInfoOriginal, pagination: eventsInfoPagination } = useSelector(
    (state) => state.certificateIssuerHome.eventsInfo,
  );
  const blockchainKillSwitch = useSelector(
    (state) => state.certificateIssuerHome.blockchainKillSwitch,
  );
  const eventsInfo = (eventsInfoOriginal || []).map((x) => ({ ...x }));
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [issueCertsEventId, setIssueCertsEventId] = useState("");
  const [issueCertsEventName, setIssueCertsEventName] = useState("");
  const [displayCsvProductionName, setDisplayCsvDataProductionName] = useState("");
  const [showDeleteEventConfirmation, setShowDeleteEventConfirmation] = useState(false);
  const [showUpdateThresholdConfirmation, setUpdateThresholdConfirmation] = useState(false);
  const [currentThreshold, setCurrentThreshold] = useState(0);
  const [updateThresholdEventId, setThresholdEventId] = useState("");
  const [updateThresholdEventName, setThresholdEventName] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showIssueCertsConfirmation, setShowIssueCertsConfirmation] = useState(false);
  const [showConfirmCsvConfirmation, setShowConfirmCsvConfirmation] = useState(false);
  const [confirmCsvEventId, setConfirmCsvEventId] = useState("");
  const [confirmCsvEventName, setConfirmCsvEventName] = useState("");
  const [showCsvData, setDisplayCsvData] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [pageSize, setPageSize] = useQueryState("pageSize", parseAsInteger.withDefault(10));
  const [productionCsvPage, setProductionCsvPage] = useState(1);
  const [productionCsvPageSize, setProductionCsvPageSize] = useState(10);

  useEffect(() => {
    dispatch(getEventsInfo({ page, pageSize }));
  }, [dispatch, page, pageSize]);

  eventsInfo.forEach((row) => {
    row.yearIteration = `${row.year}`;

    // Blockchain status logic:
    // - If certificates NOT issued yet: Show kill switch status (what WILL happen)
    // - If certificates issued: Show database status (what DID happen)
    const certificatesIssued = row.issueCertificatesStatus === "ISSUED";

    if (certificatesIssued) {
      // Show actual database status
      row.blockchainStatus = row.blockchainEnabled ? "✓ Inserted" : "✗ Not Inserted";
    } else {
      // Show kill switch status (preview what will happen)
      const killSwitchOn = blockchainKillSwitch?.issuanceEnabled === true;
      row.blockchainStatus = killSwitchOn ? "⚡Enabled" : "⊗ Disabled";
    }
  });

  const columnOptions = [
    {
      key: "productionName",
      editable: () => false,
      columnName: "Production Name",
    },
    {
      key: "yearIteration",
      editable: () => false,
      columnName: "Year/Production Run",
    },
    {
      key: "blockchainStatus",
      editable: () => false,
      columnName: "BlockForest",
      columnWidth: "130px",
    },
    {
      key: "manage",
      editable: () => false,
      columnName: "Manage",
      columnWidth: "250px",
    },
  ];

  const createIcon = (onClick, status, tooltip, i) => {
    const iconSize = 24;
    const icon = {
      [UploadStatusEnum.ENABLED]: <AiOutlineCloudUpload size={iconSize} />,
      [UploadStatusEnum.DISABLED]: <MdBlock size={iconSize} />,
      [UploadStatusEnum.UPLOADED]: <AiOutlineCloudUpload size={iconSize} />,
      [UploadStatusEnum.CONFIRMED]: <GoCheck size={iconSize} />,
      [ConfirmCsvStatus.ENABLED]: <BiCheckCircle size={iconSize} />,
      [ConfirmCsvStatus.DISABLED]: <MdBlock size={iconSize} />,
      [IssueCertificatesStatus.ENABLED]: <AiOutlineCloudUpload size={iconSize} />,
      [IssueCertificatesStatus.DISABLED]: <MdBlock size={iconSize} />,
      [IssueCertificatesStatus.ISSUED]: <GoCheck size={iconSize} />,
      [DownloadStatus.DISABLED]: <MdBlock size={iconSize} />,
      [DownloadStatus.ENABLED]: <AiOutlineDownload size={iconSize} />,
      [DownloadQrStatus.DISABLED]: <MdBlock size={iconSize} />,
      [DownloadQrStatus.ENABLED]: <AiOutlineQrcode size={iconSize} />,
      [DownloadExcelStatus.DISABLED]: <MdBlock size={iconSize} />,
      [DownloadExcelStatus.ENABLED]: <AiOutlineDownload size={iconSize} />,
      [UpdateThresholdStatus.DISABLED]: <MdBlock size={iconSize} />,
      [UpdateThresholdStatus.ENABLED]: <AiOutlineSetting size={iconSize} />,
      [DisplayCsvStatus.ENABLED]: <AiFillFileExcel size={iconSize} />,
      [DisplayCsvStatus.DISABLED]: <MdBlock size={iconSize} />,
    }[status];

    const enabledIcons = new Set([
      UpdateThresholdStatus.ENABLED,
      DownloadExcelStatus.ENABLED,
      UploadStatusEnum.ENABLED,
      UploadStatusEnum.UPLOADED,
      ConfirmCsvStatus.ENABLED,
      IssueCertificatesStatus.ENABLED,
      DisplayCsvStatus.ENABLED,
      DownloadQrStatus.ENABLED,
    ]);
    let disabled = true;
    if (enabledIcons.has(status)) {
      disabled = false;
    }

    return (
      <Tooltip key={i} label={tooltip} isOpen={disabled ? false : undefined}>
        <IconButton onClick={onClick} size="md" icon={icon} variant="ghost" disabled={disabled} />
      </Tooltip>
    );
  };

  eventsInfo.forEach((info) => {
    info.manage = [];
    const csvStatus = info.fileUploadStatus;
    const csvOnClick = () => {
      if (csvStatus === UploadStatusEnum.DISABLED || csvStatus === UploadStatusEnum.CONFIRMED) {
        return;
      }
      setFileUploadInfo({ ...info, eventName: info.productionName });
      setFileUploadType("csv");
      setShowFileUpload(true);
    };

    info.manage.push(createIcon(csvOnClick, csvStatus, "Upload csv data", "upload-csv"));

    // Confirm CSV button - only show when status is UPLOADED (not yet confirmed)
    const confirmCsvStatus =
      info.fileUploadStatus === UploadStatusEnum.UPLOADED
        ? ConfirmCsvStatus.ENABLED
        : ConfirmCsvStatus.DISABLED;
    const confirmCsvOnClick = () => {
      if (confirmCsvStatus !== ConfirmCsvStatus.ENABLED) {
        return;
      }
      setShowConfirmCsvConfirmation(true);
      setConfirmCsvEventId(info._id);
      setConfirmCsvEventName(info.productionName);
    };
    info.manage.push(
      createIcon(
        confirmCsvOnClick,
        confirmCsvStatus,
        "Confirm CSV (Generate Certificate IDs)",
        "confirm-csv",
      ),
    );

    const displayCsvStatus =
      info.fileUploadStatus === UploadStatusEnum.UPLOADED ||
      info.fileUploadStatus === UploadStatusEnum.CONFIRMED
        ? DisplayCsvStatus.ENABLED
        : DisplayCsvStatus.DISABLED;
    const displayCsvOnClick = () => {
      if (displayCsvStatus !== DisplayCsvStatus.ENABLED) {
        return;
      }
      setDisplayCsvData(true);
      setDisplayCsvDataProductionName(info.eventName);
      handleGetCsv(info._id);
    };
    info.manage.push(
      createIcon(displayCsvOnClick, displayCsvStatus, "Show CSV Data", "display-csv"),
    );

    const issueStatus = info.issueCertificatesStatus;
    const issueOnClick = () => {
      if (
        issueStatus === IssueCertificatesStatus.DISABLED ||
        issueStatus === IssueCertificatesStatus.ISSUED
      ) {
        return;
      }
      setShowIssueCertsConfirmation(true);
      setIssueCertsEventId(info._id);
      setIssueCertsEventName(info.productionName);
    };
    info.manage.push(createIcon(issueOnClick, issueStatus, "Issue certificates", "issue-certs"));

    const downloadStatus =
      info.downloadStatus === DownloadStatus.ENABLED
        ? DownloadQrStatus.ENABLED
        : DownloadQrStatus.DISABLED;
    const downloadOnClick = async () => {
      if (downloadStatus !== DownloadQrStatus.ENABLED) {
        return;
      }
      setIssueCertsEventId(info._id);
      setIssueCertsEventName(info.productionName);
      await dispatch(downloadQrs(info._id));
    };
    info.manage.push(
      createIcon(downloadOnClick, downloadStatus, "Download QR codes as zip file", "download-qr"),
    );

    const downloadStatsStatus =
      info.downloadStatus === DownloadStatus.ENABLED
        ? DownloadExcelStatus.ENABLED
        : DownloadExcelStatus.DISABLED; // download stats and download qr share the same state
    const downloadStatsOnClick = async () => {
      // disable if the event state does not allow for download of stats yet
      if (downloadStatsStatus !== DownloadExcelStatus.ENABLED) {
        return;
      }
      setIssueCertsEventId(info._id);
      setIssueCertsEventName(info.productionName);
      const { body } = (await dispatch(downloadScanStats(info._id))).payload;
      processAndDownloadExcel(body.statsData, info.productionName);
    };

    info.manage.push(
      createIcon(
        downloadStatsOnClick,
        downloadStatsStatus,
        "Download Scan Statistics",
        "download-stats",
      ),
    );

    const updateThresholdStatus =
      info.downloadStatus === DownloadStatus.ENABLED
        ? UpdateThresholdStatus.ENABLED
        : UpdateThresholdStatus.DISABLED; // can only update if the cert has been approved, same as download
    const updateThresholdOnClick = () => {
      if (updateThresholdStatus !== UpdateThresholdStatus.ENABLED) {
        return;
      }

      setThresholdEventId(info._id);
      setThresholdEventName(info.productionName);
      setCurrentThreshold(info.threshold);
      setUpdateThresholdConfirmation(true);
    };
    info.manage.push(
      createIcon(
        updateThresholdOnClick,
        updateThresholdStatus,
        "Update Scan Threshold",
        "update-threshold",
      ),
    );

    const deleteOnClick = () => {
      setShowDeleteEventConfirmation(true);
      setDeleteEventId(info._id);
      setDeleteEventName(info.productionName);
    };

    const isDeleteDisabled = info.issueCertificatesStatus === IssueCertificatesStatus.ISSUED;
    const deleteIcon = (
      <Tooltip key="delete-btn" label={"Delete Production"}>
        <IconButton
          boxShadow="none !important"
          onClick={deleteOnClick}
          size="md"
          icon={<AiOutlineDelete size={24} />}
          variant="ghost"
          disabled={isDeleteDisabled}
          cursor={isDeleteDisabled ? "not-allowed" : ""}
        />
      </Tooltip>
    );
    info.manage.push(deleteIcon);
  });

  const handleDeleteEvent = async (e) => {
    e.preventDefault();
    await dispatch(deleteEvent(deleteEventId));
    setShowDeleteEventConfirmation(false);
  };

  const handleClose = (setter) => (shouldRefreshTable) => {
    setter(false);
    if (shouldRefreshTable) {
      dispatch(getEventsInfo({ page, pageSize }));
    }
  };

  const handleGetCsv = async (displayCsvProductionId) => {
    const data = (await dispatch(getCertData(displayCsvProductionId))).payload;
    setCsvData(data.body.certData);
    return data.body.certData;
  };

  const processData = (csvData) => {
    if (!csvData || !csvData.rows) {
      return [];
    }

    const res = [];
    const set = new Set();
    for (const key in csvData.rows) {
      const batchNumber = csvData.rows[key][0];
      // skip duplicated value
      if (set.has(batchNumber)) {
        continue;
      }

      res.push(csvData.rows[key]);
      set.add(batchNumber);
    }

    return res;
  };

  const processColumn = (csvData) => {
    if (!csvData || !csvData.columns) {
      return [];
    }

    const columns = csvData.columns;
    const columnOptions = columns.map((columnName, i) => {
      return {
        key: i,
        editable: () => false,
        columnWidth: 100,
        columnName,
      };
    });
    return columnOptions;
  };

  const handleCloseModal = () => {
    setDisplayCsvData(false);
    setCsvData(null);
  };

  const handleUpdateThreshold = async (e) => {
    e.preventDefault();
    await dispatch(updateThreshold({ updateThresholdEventId, currentThreshold }));
    setUpdateThresholdConfirmation(false);
  };

  return (
    <Flex flexDirection="column" align="center" justify="center" width="100%">
      <FileUpload
        isOpen={showFileUpload}
        type={fileUploadType}
        fileUploadInfo={fileUploadInfo}
        handleClose={handleClose(setShowFileUpload)}
      />
      <Modal
        isOpen={showCsvData && csvData !== null && csvData !== undefined}
        onClose={handleCloseModal}
      >
        <ModalOverlay />
        <ModalContent maxW="90%" h="100%">
          <ModalHeader>Production CSV Data</ModalHeader>
          <ModalBody>
            <EditableTable
              tableData={processData(csvData)}
              columnOptions={processColumn(csvData)}
              tableName={displayCsvProductionName}
              page={productionCsvPage}
              pageSize={productionCsvPageSize}
              setPage={setProductionCsvPage}
              setPageSize={setProductionCsvPageSize}
            />
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
      {showIssueCertsConfirmation && (
        <IssueCertsConfirmation
          eventName={issueCertsEventName}
          isOpen={showIssueCertsConfirmation}
          eventId={issueCertsEventId}
          handleClose={handleClose(setShowIssueCertsConfirmation)}
        />
      )}
      {showConfirmCsvConfirmation && (
        <ConfirmCsvConfirmation
          showConfirmCsvConfirmation={showConfirmCsvConfirmation}
          eventId={confirmCsvEventId}
          eventName={confirmCsvEventName}
          handleClose={() => setShowConfirmCsvConfirmation(false)}
        />
      )}
      <DeleteConfirmation
        header={`Delete ${deleteEventName}`}
        handleDelete={handleDeleteEvent}
        isOpen={showDeleteEventConfirmation}
        handleClose={handleClose(setShowDeleteEventConfirmation)}
      />
      <UpdateThresholdConfirmation
        header={`Update threshold for production ${updateThresholdEventName}`}
        handleUpdate={handleUpdateThreshold}
        isOpen={showUpdateThresholdConfirmation}
        handleClose={handleClose(setUpdateThresholdConfirmation)}
        input={currentThreshold}
        setInput={setCurrentThreshold}
      />
      <EditableTable
        tableName="Production Information"
        tableData={eventsInfo}
        columnOptions={columnOptions}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        totalItems={eventsInfoPagination?.totalCount || 0}
        totalPages={eventsInfoPagination?.totalPages || 0}
      />
      <Button
        margin="20px 0"
        onClick={() => setShowNewEventForm(true)}
        leftIcon={<GoDiffAdded width={24} />}
        variant="outline"
      >
        Add a new production
      </Button>
      <CreateEventConfirmation
        isOpen={showNewEventForm}
        handleClose={handleClose(setShowNewEventForm)}
      />
    </Flex>
  );
}

export default EventsInfoTable;
